/**
 * Recessed-light ROW — a line the user draws, filled with cans at a fixed
 * spacing.
 *
 * The complement to the ceiling editor's automatic grid (`ceilingLightPlan` →
 * `layoutRecessedLights`, which decides the layout for you). Here the user has
 * decided where the row goes and the app only has to space it.
 *
 * WHERE the cans land relative to the line is a real choice, because the
 * leftover after fitting whole gaps has to go somewhere:
 *
 *   centred  exact spacing, leftover split as a margin at both ends. The
 *            clicks bound the row; no can sits on either of them unless the
 *            length happens to divide evenly.
 *   start    exact spacing anchored at the FIRST click, remainder left at the
 *            far end. What you want when aligning a can to something.
 *   ends     a can on each click, spacing TIGHTENED to the nearest even
 *            division that fits. Endpoints exact, spacing at most what you
 *            asked for and usually less.
 *
 * Pure: no React, no canvas, no Firestore.
 */

import {
  deriveFlatCeilingY,
  seatCandidates,
} from '@shared-lib/lighting/ceilingSeat';
import LIGHTING_CONSTANTS from '@shared-lib/lighting/constants';
import { RECESSED_LIGHT_DIMENSIONS } from './ceilingLightPlan';

/** 4 ft. The spacing the tool opens on; the user can change it per row. */
export const DEFAULT_ROW_SPACING_M = 1.2192;

/**
 * What the tool offers. Whole and half feet around the 4' default — the range
 * a recessed row is actually laid out on. Presets rather than free entry: the
 * control's whole job is a round number, and parsing `4'6"` is a liability.
 */
export const ROW_SPACING_CHOICES = [2, 2.5, 3, 3.5, 4, 4.5, 5, 6].map(
  (feet) => ({
    feet,
    metres: feet * 0.3048,
    label: Number.isInteger(feet) ? `${feet}'` : `${Math.floor(feet)}' 6"`,
  })
);

/** Below this a drag is a click, not a line — one can, no direction. */
const MIN_ROW_LENGTH_M = 0.02;

/** How the row sits on the line. See the module comment for the trade-off. */
export const ROW_ALIGNMENTS = [
  { id: 'centred', label: 'Centred' },
  { id: 'start', label: 'From start' },
  { id: 'ends', label: 'Fit ends' },
];
export const DEFAULT_ROW_ALIGNMENT = 'centred';

/**
 * How far along the line each can sits, as distances from the start.
 *
 * Split out from the world-space mapping because THIS is the part with the
 * decision in it — the rest is projecting onto the segment.
 *
 * @param {number} length - Segment length, metres
 * @param {number} spacing - Requested metres between cans
 * @param {string} align - One of ROW_ALIGNMENTS
 * @returns {number[]} Distances along the line
 */
const offsetsAlong = (length, spacing, align) => {
  if (align === 'ends') {
    // A can on each click, so the spacing has to give. It only ever TIGHTENS:
    // `ceil` treats the requested spacing as a maximum, which is how a lighting
    // spacing is meant — 4' means "no further apart than 4'". Rounding to
    // nearest would let a 14' line at 4' come back at 4'8", wider than asked,
    // and lands on a floating-point coin-toss at the exact half.
    // The epsilon keeps an exact division (12' at 4') from ticking up a gap.
    const gaps = Math.max(1, Math.ceil(length / spacing - 1e-9));
    const step = length / gaps;
    return Array.from({ length: gaps + 1 }, (_, i) => i * step);
  }

  const gaps = Math.floor(length / spacing);
  if (align === 'start') {
    // Anchored on the first click, exact spacing, remainder at the far end.
    return Array.from({ length: gaps + 1 }, (_, i) => i * spacing);
  }
  // Centred: exact spacing, the leftover halved into a margin at each end.
  const margin = (length - gaps * spacing) / 2;
  return Array.from({ length: gaps + 1 }, (_, i) => margin + i * spacing);
};

/**
 * Points along a segment for a row of cans.
 *
 * A segment shorter than one gap gets a single can — the user asked for a
 * light here, and refusing because the line is short would be unhelpful.
 *
 * @param {{x: number, z: number}} a - Line start, world
 * @param {{x: number, z: number}} b - Line end, world
 * @param {number} [spacing] - Metres between cans
 * @param {string} [align] - One of ROW_ALIGNMENTS
 * @returns {Array<{x: number, z: number}>}
 */
export const rowCandidates = (
  a,
  b,
  spacing = DEFAULT_ROW_SPACING_M,
  align = DEFAULT_ROW_ALIGNMENT
) => {
  if (!a || !b || !(spacing > 0)) return [];
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  if (length < MIN_ROW_LENGTH_M) return [{ x: a.x, z: a.z }];

  const ux = dx / length;
  const uz = dz / length;
  return offsetsAlong(length, spacing, align).map((d) => ({
    x: a.x + ux * d,
    z: a.z + uz * d,
  }));
};

/**
 * Seat a row's candidates on the room's ceiling and build the can objects.
 *
 * Seating goes through the SAME `seatCandidates` the automatic layout uses, so
 * a row on a sloped facet tilts with it, and a candidate with no ceiling above
 * is DROPPED rather than floated at some fallback height — a can hanging in
 * open air is worse than a gap in the row.
 *
 * @param {Object} params
 * @param {{x: number, z: number}} params.a - Line start, world
 * @param {{x: number, z: number}} params.b - Line end, world
 * @param {number} [params.spacing] - Metres between cans
 * @param {string} [params.align] - One of ROW_ALIGNMENTS
 * @param {Object} params.room - The room the row is drawn in
 * @param {number} [params.now] - Injected for deterministic ids in tests
 * @returns {Array<Object>} Recessed-light objects, ready to add to the room
 */
export const planRecessedRow = ({
  a,
  b,
  spacing = DEFAULT_ROW_SPACING_M,
  align = DEFAULT_ROW_ALIGNMENT,
  room,
  now = Date.now(),
}) => {
  const candidates = rowCandidates(a, b, spacing, align);
  if (!candidates.length) return [];

  const ceilings = room?.ceilings || [];
  // An ELEVATION, not a height: null means nothing to hang from, and
  // seatCandidates then emits nothing rather than seating at world zero.
  const fallbackY = deriveFlatCeilingY(ceilings, room?.walls || []);
  const seated = seatCandidates(
    candidates,
    ceilings,
    fallbackY,
    LIGHTING_CONSTANTS
  );

  const dims = RECESSED_LIGHT_DIMENSIONS;
  // A row moves as a row. `fixtureGroupId` is the codebase's existing "these
  // travel together" key — `walkMoveGroup` and `cascadeMovement` both close
  // over it, and the plan highlights siblings when you select one — so the row
  // gets one rather than a fourth relationship layer being invented for it.
  //
  // Only when there is more than one can: a group of one is not a row, and
  // tagging it would make a lone light read as part of an assembly.
  const groupId = seated.length > 1 ? `recessed-row_${now}` : null;

  return seated.map((seat, index) => ({
    id: `ceiling-light_${now}_row_${index}`,
    type: 'ceiling-light',
    label: seated.length > 1 ? `Recessed Light ${index + 1}` : 'Recessed Light',
    category: { 'ceiling-light': true },
    // The recessed renderer treats center as the body midpoint, half the can
    // depth below the ceiling surface ALONG THE FACET NORMAL — so the trim
    // ring lands flush on a sloped facet as well as a flat one. Same contract
    // as ceilingLightPlan's grid; a plain `y - depth/2` would bury the can on
    // a slope.
    center: {
      x: seat.surface.x - seat.normal.x * (dims.y / 2),
      y: seat.surface.y - seat.normal.y * (dims.y / 2),
      z: seat.surface.z - seat.normal.z * (dims.y / 2),
    },
    dimensions: { ...dims },
    rotation: seat.rotation,
    // Without this the 3D scene never rebuilds lighting for the can — it comes
    // in unlit and behind the ceiling. See reference-docs/lighting-catalog.
    parentCeilingId: seat.ceilingId ?? null,
    proceduralStyle: 'recessed',
    ...(groupId ? { fixtureGroupId: groupId } : {}),
    addedBy: 'recessed_row_tool',
    lastModified: new Date(now).toISOString(),
  }));
};
